import { openDB } from 'idb';
import { cloudReferenceKeys, isFileReference } from './cloudFileState';
import {writeQueuedRow,type QueueStage} from './syncQueue';
import {cloudReadFetch} from './cloudRead';

export type CatalogRow = {
  id: string; user_id: string; document_type: 'quote' | 'invoice'; payload: unknown;
  version: number; updated_at: string; deleted_at: string | null; cloud_revision?: number;
  trash_expires_at?:string|null;purged_at?:string|null;trash_legacy?:boolean;
};
type Page = { generation: number; total: number; rows: CatalogRow[]; next: string | null; changed?: boolean;serverNow?:string;accountTotal?:number;baseGeneration?:number };
type Snapshot = { userId:string;generation:number;rows:CatalogRow[] };
type Context = { userId: string; token: string; signal: AbortSignal; queued?:boolean;onQueueStage?:(stage:QueueStage)=>void };
const cache = (uid: string) => openDB(`bilaflow-r2-catalog-${uid}`, 1, { upgrade(db) { db.createObjectStore('cache'); } });
export const checksum = async (text: string) => [...new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text)))].map((x) => x.toString(16).padStart(2, '0')).join('');
const request = async (path: string, context: Context, init: RequestInit = {}) => {
  const response = await cloudReadFetch(path, { ...init, signal:context.signal,
    cache: 'no-store', headers: { authorization: `Bearer ${context.token}`, 'content-type': 'application/json','x-bilaflow-sync-protocol':'r2-trash-v1', ...init.headers } },path==='/api/sync/file-status');
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string; changed?: boolean };
    throw new Error(body.changed ? 'cloud_snapshot_changed' : body.error || `Cloud request failed (${response.status})`);
  }
  return response;
};

type RequiredObject={key:string;sha256:string};
async function readCatalogFiles(rows:CatalogRow[],pending:RequiredObject[],generation:number,context:Context){
  const bodyKeys=new Set(pending.map(item=>item.key)),keys=[...cloudReferenceKeys(rows)].filter(key=>!bodyKeys.has(key));
  const files=new Map<string,boolean>(),objects=new Map<string,string>();
  const fence=()=>request(`/api/sync/r2-documents?generation=${generation}&since=${generation}`,context);
  if(!keys.length&&!pending.length){await fence();return{files,objects};}
  const count=Math.max(Math.ceil(keys.length/100),Math.ceil(pending.length/8));
  const batch=async(index:number)=>{
    const heads=keys.slice(index*100,(index+1)*100),bodies=pending.slice(index*8,(index+1)*8);
    const value=await(await request('/api/sync/file-status',context,{method:'POST',body:JSON.stringify({keys:heads,read:{protocol:'catalog-read-v1',objects:bodies.map(item=>item.key),...(index===0?{generation}:{})}})})).json() as {
      files?:Record<string,boolean>;objects?:Record<string,string>;read?:{protocol?:unknown;generation?:unknown};
    };
    // Older edge bundles know only the legacy presence request. Fall back to
    // its full generation/presence/content path, never accept a partial proof.
    if(value.read===undefined)return false;
    if(value.read?.protocol!=='catalog-read-v1'||index===0&&value.read.generation!==generation)throw new Error('invalid_cloud_catalog');
    for(const key of [...heads,...bodies.map(item=>item.key)]){
      if(typeof value.files?.[key]!=='boolean')throw new Error('incomplete_file_manifest');
      files.set(key,value.files[key]);
    }
    for(const item of bodies){
      const text=value.objects?.[item.key];
      if(typeof text!=='string'||await checksum(text)!==item.sha256)throw new Error('document_integrity_failed');
      objects.set(item.key,text);
    }
    return true;
  };
  if(await batch(0)){
    for(let offset=1;offset<count;offset+=4){
      const settled=await Promise.allSettled(Array.from({length:Math.min(4,count-offset)},(_,i)=>batch(offset+i)));
      const failure=settled.find(result=>result.status==='rejected');if(failure?.status==='rejected')throw failure.reason;
      // Mixed bundles may still serve an older response on a later page.
      if(settled.some(result=>result.status==='fulfilled'&&!result.value))return legacy();
    }
    return{files,objects};
  }
  return legacy();
  async function legacy(){
    await fence();const files=await fileAvailability(rows,context),objects=new Map<string,string>();
    for(let offset=0;offset<pending.length;offset+=8){
      const batch=pending.slice(offset,offset+8),value=await(await request(`/api/sync/r2-documents?objects=${encodeURIComponent(JSON.stringify(batch.map(item=>item.key)))}`,context)).json() as {objects:Record<string,string>};
      for(const item of batch){const text=value.objects?.[item.key];if(typeof text!=='string'||await checksum(text)!==item.sha256)throw new Error('document_integrity_failed');objects.set(item.key,text);}
    }
    return{files,objects};
  }
}

export async function readR2Catalog(context: Context) {
  const db = await Promise.resolve().then(() => cache(context.userId)).catch(() => null);
  const get = async (key: string) => db?.get('cache', key).catch(() => undefined);
  const put = async (value: unknown, key: string) => { await db?.put('cache', value, key).catch(() => {}); };
  let snapshot=await get('verified-snapshot-v1') as Snapshot|undefined;
  if(snapshot&&(snapshot.userId!==context.userId||!Number.isSafeInteger(snapshot.generation)||snapshot.generation<0
    ||!Array.isArray(snapshot.rows)||snapshot.rows.some(row=>row.user_id!==context.userId)
    ||new Set(snapshot.rows.map(row=>row.id)).size!==snapshot.rows.length))snapshot=undefined;
  try {
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const query=snapshot?`since=${snapshot.generation}`:'';
        const first = await (await request(`/api/sync/r2-documents${query?'?'+query:''}`, context)).json() as Page;
        if(snapshot&&(first.baseGeneration!==snapshot.generation||!Number.isSafeInteger(first.accountTotal))){snapshot=undefined;continue;}
        let page = first;
        let rows: CatalogRow[] = [];
        const seen = new Set<string>();
        let after = '';
        for (;;) {
          if (page.generation !== first.generation || page.total !== first.total || page.baseGeneration!==first.baseGeneration||page.accountTotal!==first.accountTotal||!Array.isArray(page.rows)) throw new Error('cloud_snapshot_changed');
          if(!Number.isSafeInteger(page.total)||page.total<0||!Number.isSafeInteger(page.generation)||page.generation<0)throw new Error('invalid_cloud_catalog');
          for (const row of page.rows) {
            if (row.user_id !== context.userId || seen.has(row.id) || (after && row.id <= after)) throw new Error('invalid_cloud_catalog');
            seen.add(row.id); rows.push(row);
          }
          // Commit a page only after validating its ownership and progression.
          await put(page, `page:${query}:${after}`);
          if (rows.length >= first.total) break;
          if (!page.next || page.next <= after || !page.rows.length) throw new Error('incomplete_cloud_catalog');
          after = page.next;
          const saved = await get(`page:${query}:${after}`) as Page | undefined;
          page = saved?.generation === first.generation ? saved
            : await (await request(`/api/sync/r2-documents?after=${encodeURIComponent(after)}&generation=${first.generation}${query?'&'+query:''}`, context)).json() as Page;
        }
        if (rows.length !== first.total) throw new Error('incomplete_cloud_catalog');
        if(snapshot){
          const merged=new Map(snapshot.rows.map(row=>[row.id,row]));
          for(const row of rows){
            if(!Number.isSafeInteger(row.cloud_revision)||row.cloud_revision!<=snapshot.generation||row.cloud_revision!>first.generation)throw new Error('invalid_cloud_catalog');
            merged.set(row.id,row);
          }
          // A privileged hard reset/deletion has no recoverable tombstone.
          // Never retain a cached record that the server no longer counts.
          if(merged.size!==first.accountTotal){snapshot=undefined;continue;}
          rows=[...merged.values()].sort((a,b)=>a.id.localeCompare(b.id));
        }
        const serverNow=first.serverNow?Date.parse(first.serverNow):Date.now();
        const expired=(row:CatalogRow)=>Boolean(row.deleted_at&&row.trash_expires_at&&Date.parse(row.trash_expires_at)<=serverNow);
        const activeRows=rows.filter(row=>!row.purged_at&&!expired(row)),expected=new Map<string,string>(),verified=new Map<string,string>(),verifiedSources=new Map<string,string>();
        let verifiedBytes=0;
        const remember=(key:string,text:string)=>{
          verifiedBytes-=(verified.get(key)?.length||0)*2;verified.delete(key);
          while(verifiedBytes+text.length*2>8*1024*1024&&verified.size){const oldest=verified.keys().next().value!;verifiedBytes-=verified.get(oldest)!.length*2;verified.delete(oldest);}
          if(text.length*2<=8*1024*1024){verified.set(key,text);verifiedBytes+=text.length*2;}
        };
        const prepared=new Map<string,RequiredObject&{cached:boolean}>();
        for (const row of rows) {
          if(row.purged_at||expired(row))continue;
          const manifest = row.payload as { recordType?: string; object?: unknown };
          if (manifest?.recordType !== 'r2-document-v1' || !isFileReference(manifest.object)) continue;
          const object = manifest.object;
          if (!object.key.startsWith(`${context.userId}/`) || !/^[a-f0-9]{64}$/.test(object.sha256 || '')) throw new Error('invalid_document_manifest');
          if(expected.has(object.key)&&expected.get(object.key)!==object.sha256)throw new Error('invalid_document_manifest');
          expected.set(object.key,object.sha256!);
          const saved = await get(`object:${row.id}`) as { key: string; sha256: string; text: string } | undefined;
          const cached=Boolean(saved&&saved.key===object.key&&saved.sha256===object.sha256&&typeof saved.text==='string'&&await checksum(saved.text)===object.sha256);
          if(cached){verifiedSources.set(object.key,row.id);remember(object.key,saved!.text);}
          prepared.set(row.id,{key:object.key,sha256:object.sha256!,cached});
        }
        const pending=[...expected].filter(([key])=>!verifiedSources.has(key)).map(([key,sha256])=>({key,sha256}));
        // One bounded read can confirm generation, establish presence and fetch
        // missing JSON. Cached JSON still receives a physical presence check.
        const {files:availability,objects:downloaded}=await readCatalogFiles(activeRows,pending,first.generation,context);
        const legacyIds = new Set<string>();
        const result: CatalogRow[] = [];
        for (const row of rows) {
          context.signal.throwIfAborted();
          const manifest = row.payload as { recordType?: string; object?: unknown; files?: unknown[] };
          if(row.purged_at||manifest?.recordType==='purged-document-v1'||expired(row)){
            if(row.purged_at)await db?.delete('cache',`object:${row.id}`).catch(()=>{});
            const index=(row.payload as {index?:Record<string,unknown>})?.index||{};
            result.push({...row,payload:{id:row.id,number:typeof index.number==='string'?index.number:'',customerName:typeof index.customerName==='string'?index.customerName:'',items:[]}});
            continue;
          }
          if (manifest?.recordType !== 'r2-document-v1') { legacyIds.add(row.id); result.push(row); continue; }
          if (!isFileReference(manifest.object) || !manifest.object.key.startsWith(`${context.userId}/`) || !/^[a-f0-9]{64}$/.test(manifest.object.sha256 || '')) throw new Error('invalid_document_manifest');
          const object = manifest.object;
          if (!availability.get(object.key)) throw new Error('cloud_document_missing');
          let text=verified.get(object.key)??downloaded.get(object.key),repairCache=false;
          if(text===undefined&&verifiedSources.has(object.key)){
            // Keep verified text bounded on large accounts. Revalidate an
            // evicted cache entry before parsing; another tab may have changed it.
            const saved=await get(`object:${verifiedSources.get(object.key)}`) as {key?:string;sha256?:string;text?:string}|undefined;
            if(saved?.key===object.key&&saved.sha256===object.sha256&&typeof saved.text==='string'&&await checksum(saved.text)===object.sha256)text=saved.text;
            else{ text=await(await request(`/api/sync/r2-documents?object=${encodeURIComponent(object.key)}`,context)).text();if(await checksum(text)!==object.sha256)throw new Error('document_integrity_failed');repairCache=true; }
          }
          if(typeof text!=='string')throw new Error('document_integrity_failed');
          if(!prepared.get(row.id)?.cached||repairCache){
            await put({ key: object.key, sha256: object.sha256, text }, `object:${row.id}`);
          }
          const payload: unknown = JSON.parse(text);
          const actualKeys = [...cloudReferenceKeys(payload)].sort();
          const manifestKeys = [...cloudReferenceKeys(manifest.files)].sort();
          if (JSON.stringify(actualKeys) !== JSON.stringify(manifestKeys)) throw new Error('document_manifest_mismatch');
          result.push({ ...row, payload });
        }
        await put({userId:context.userId,generation:first.generation,rows},'verified-snapshot-v1');
        return { rows: result, legacyIds, availability };
      } catch (error) {
        if (!(error instanceof Error) || error.message !== 'cloud_snapshot_changed' || attempt === 2) throw error;
        snapshot=undefined;
      }
    }
    throw new Error('cloud_snapshot_changed');
  } finally { db?.close(); }
}

export async function fileAvailability(rows: unknown, context: Context) {
  const keys = [...cloudReferenceKeys(rows)];
  const availability = new Map<string, boolean>();
  // Bounded parallel batches reduce round trips without skipping presence checks.
  for (let offset = 0; offset < keys.length; offset += 400) {
    await Promise.all(Array.from({length:Math.ceil(Math.min(400,keys.length-offset)/100)},async(_,index)=>{
      const batch=keys.slice(offset+index*100,offset+(index+1)*100);
      const result=await (await request('/api/sync/file-status',context,{method:'POST',body:JSON.stringify({keys:batch})})).json() as {files:Record<string,boolean>};
      for(const key of batch){
        if(typeof result.files?.[key]!=='boolean')throw new Error('incomplete_file_manifest');
        availability.set(key,result.files[key]);
      }
    }));
  }
  return availability;
}

export async function writeR2Row(row: CatalogRow, context: Context) {
  if(context.queued)return writeQueuedRow(row,context);
  return (await request('/api/sync/r2-documents', context, { method: 'POST', body: JSON.stringify(row) })).json() as Promise<{ revision: number;deletedAt?:string|null;trashExpiresAt?:string|null }>;
}

export async function cloudTrashAction(id:string,revision:number,action:'trash'|'restore',context:Context){
  return (await request('/api/trash',context,{method:'POST',body:JSON.stringify({id,revision,action})})).json() as Promise<{revision:number;deletedAt:string|null;trashExpiresAt:string|null}>;
}
